import React, { useState } from 'react'
import { CalendarRange } from 'lucide-react'
import { RANGE_PRESETS } from './model'
import { rangeProblem } from '../../utils/dateRange'

// The dashboard's one range control. It decides *what* is asked for and
// nothing else: the dates actually shown come back from the server in
// `range`, so this never computes a preset's dates itself.
//
// A custom range is only handed up once both ends are real dates in order.
// Half-typed input stays local, so editing one side never fires a request
// for a range nobody meant.
export default function RangePicker({ value, onChange }) {
  const isCustom = value.preset === 'custom'
  const [customOpen, setCustomOpen] = useState(isCustom)
  const [draftFrom, setDraftFrom] = useState(value.dateFrom || '')
  const [draftTo, setDraftTo] = useState(value.dateTo || '')
  const [error, setError] = useState(null)

  const pickPreset = (preset) => {
    setCustomOpen(false)
    setError(null)
    if (value.preset !== preset) onChange({ preset, dateFrom: null, dateTo: null })
  }

  const commit = (from, to) => {
    setDraftFrom(from)
    setDraftTo(to)
    if (!from || !to) { setError(null); return }
    const problem = rangeProblem(from, to)
    setError(problem)
    if (!problem && (value.preset !== 'custom' || value.dateFrom !== from || value.dateTo !== to)) {
      onChange({ preset: 'custom', dateFrom: from, dateTo: to })
    }
  }

  const active = customOpen ? 'custom' : value.preset

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
      <div
        className="pd-range"
        role="group"
        aria-label="Date range"
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}
      >
        {RANGE_PRESETS.map((p) => (
          <RangeButton key={p.id} active={active === p.id} onClick={() => pickPreset(p.id)}>
            {p.label}
          </RangeButton>
        ))}
        <RangeButton active={active === 'custom'} onClick={() => setCustomOpen(true)}>
          <CalendarRange size={12} /> Custom
        </RangeButton>
      </div>
      {customOpen && (
        <div className="pd-range-custom" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="date"
            aria-label="Start date"
            value={draftFrom}
            max={draftTo || undefined}
            onChange={(e) => commit(e.target.value, draftTo)}
            style={{ ...dateInputStyle, borderColor: error ? 'var(--red)' : 'var(--border)' }}
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>to</span>
          <input
            type="date"
            aria-label="End date"
            value={draftTo}
            min={draftFrom || undefined}
            onChange={(e) => commit(draftFrom, e.target.value)}
            style={{ ...dateInputStyle, borderColor: error ? 'var(--red)' : 'var(--border)' }}
          />
        </div>
      )}
      {error && (
        <span role="alert" style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>
          {error}
        </span>
      )}
    </div>
  )
}

function RangeButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        borderRadius: 6, cursor: 'pointer',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        transition: 'all .12s',
      }}
    >
      {children}
    </button>
  )
}

const dateInputStyle = {
  padding: '5px 8px', fontSize: 12, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
}
